// A line about a result, read off its URL and nothing else: the site's name
// when it is one we know, what kind of page it is, a title from the path,
// and a date when the path has one. No request is made for it -- fetching
// the page, or even its favicon, would tell its host that this search
// returned it.
//
//   en.wikipedia.org/wiki/Alan_Turing               Wikipedia · Alan Turing
//   stanforddaily.com/2020/04/27/student-survey-…   The Stanford Daily · Student Survey to… · Apr 27, 2020
//   uslocalgyms.com/california-gyms/palo_alto/pilates/   California Gyms › Palo Alto › Pilates
//
// The host is shown above the line already, so a site we don't know is not
// named again: "ocweekly" would only come out as "Ocweekly".

export interface Description {
  site?: string; // a name we know; the host otherwise speaks for itself
  kind?: string; // Blog, News, Docs, ...
  title?: string;
  date?: string;
}

// Sites whose names their domains don't spell.
const KNOWN: Record<string, string> = {
  "wikipedia.org": "Wikipedia", "wiktionary.org": "Wiktionary", "wikimedia.org": "Wikimedia",
  "github.com": "GitHub", "gitlab.com": "GitLab",
  "stackoverflow.com": "Stack Overflow", "stackexchange.com": "Stack Exchange", "superuser.com": "Super User",
  "serverfault.com": "Server Fault", "askubuntu.com": "Ask Ubuntu",
  "reddit.com": "Reddit", "youtube.com": "YouTube", "youtu.be": "YouTube", "imdb.com": "IMDb",
  "linkedin.com": "LinkedIn", "twitter.com": "X", "x.com": "X", "facebook.com": "Facebook", "instagram.com": "Instagram",
  "nytimes.com": "The New York Times", "washingtonpost.com": "The Washington Post", "wsj.com": "The Wall Street Journal",
  "theguardian.com": "The Guardian", "theatlantic.com": "The Atlantic", "newyorker.com": "The New Yorker",
  "bbc.co.uk": "BBC", "bbc.com": "BBC", "cnn.com": "CNN", "npr.org": "NPR", "reuters.com": "Reuters",
  "apnews.com": "AP News", "bloomberg.com": "Bloomberg", "forbes.com": "Forbes", "techcrunch.com": "TechCrunch",
  "theverge.com": "The Verge", "wired.com": "Wired", "arstechnica.com": "Ars Technica",
  "stanforddaily.com": "The Stanford Daily", "stanfordreview.org": "The Stanford Review",
  "arxiv.org": "arXiv", "pubmed.ncbi.nlm.nih.gov": "PubMed", "ncbi.nlm.nih.gov": "NCBI", "nih.gov": "NIH",
  "nature.com": "Nature", "sciencedirect.com": "ScienceDirect", "springer.com": "Springer", "ieee.org": "IEEE",
  "acm.org": "ACM", "researchgate.net": "ResearchGate", "semanticscholar.org": "Semantic Scholar",
  "developer.mozilla.org": "MDN", "docs.python.org": "Python docs", "pypi.org": "PyPI", "npmjs.com": "npm",
  "cran.r-project.org": "CRAN", "mathworks.com": "MathWorks",
  "news.ycombinator.com": "Hacker News", "ycombinator.com": "Y Combinator",
  "britannica.com": "Britannica", "goodreads.com": "Goodreads", "tripadvisor.com": "Tripadvisor", "yelp.com": "Yelp",
  "craigslist.org": "Craigslist", "ebay.com": "eBay", "etsy.com": "Etsy", "amazon.com": "Amazon",
  "apple.com": "Apple", "microsoft.com": "Microsoft", "learn.microsoft.com": "Microsoft Learn", "google.com": "Google",
  "ibm.com": "IBM", "research.ibm.com": "IBM Research", "aws.amazon.com": "AWS", "cloud.google.com": "Google Cloud",
  "medium.com": "Medium", "substack.com": "Substack", "quora.com": "Quora",
  "webmd.com": "WebMD", "mayoclinic.org": "Mayo Clinic", "clevelandclinic.org": "Cleveland Clinic",
  "allrecipes.com": "Allrecipes", "espn.com": "ESPN",
  "stanford.edu": "Stanford", "stanfordhealthcare.org": "Stanford Health Care", "mit.edu": "MIT",
  "harvard.edu": "Harvard", "berkeley.edu": "UC Berkeley", "yale.edu": "Yale", "princeton.edu": "Princeton",
  "deepai.org": "DeepAI", "hostinger.com": "Hostinger", "proton.me": "Proton", "milvus.io": "Milvus",
  "metabase.com": "Metabase", "launchpad.net": "Launchpad", "waymo.com": "Waymo",
};

// The second-level labels a country registry hands out: "bbc.co.uk",
// "bvrit.ac.in", "cuc.edu.co" are the sites, not "co.uk" or "edu.co".
const SLD = new Set(["co", "com", "org", "net", "ac", "edu", "gov", "or", "ne", "go", "mil", "gob", "sch", "nic", "ltd", "plc", "info", "biz"]);

const LANG: Record<string, string> = {
  de: "German", fr: "French", es: "Spanish", it: "Italian", ja: "Japanese", zh: "Chinese",
  ru: "Russian", pt: "Portuguese", nl: "Dutch", pl: "Polish", ko: "Korean", sv: "Swedish",
};

// What a subdomain or a path segment says about the page.
const KIND: Record<string, string> = {
  blog: "Blog", blogs: "Blog", news: "News", newsroom: "News", press: "News", "press-release": "News",
  "press-releases": "News", "latest-news": "News", docs: "Docs", doc: "Docs", documentation: "Docs",
  developer: "Docs", developers: "Docs", reference: "Docs", ref: "Docs", man: "Docs", manual: "Docs",
  forum: "Forum", forums: "Forum", community: "Forum", discuss: "Forum", discussion: "Forum",
  support: "Help", help: "Help", faq: "FAQ", faqs: "FAQ", tutorials: "Tutorial", tutorial: "Tutorial",
  learn: "Guide", guide: "Guide", guides: "Guide", jobs: "Jobs", careers: "Jobs",
  events: "Events", event: "Events", calendar: "Events",
  recipe: "Recipe", recipes: "Recipe", podcast: "Podcast", podcasts: "Podcast",
  video: "Video", videos: "Video", watch: "Video", review: "Review", reviews: "Review",
  questions: "Question", product: "Product", products: "Product", "product-page": "Product", dp: "Product",
  shop: "Shop", store: "Shop", collections: "Shop", catalogue: "Shop", catalog: "Shop",
  publication: "Paper", publications: "Paper", paper: "Paper", papers: "Paper", proceedings: "Paper",
  opinion: "Opinion", interview: "Interview", interviews: "Interview", people: "People", faculty: "People",
  team: "People", "our-people": "People", staff: "People", profile: "Profile",
};
const KIND_ON_SUBDOMAIN = new Set(["blog", "news", "docs", "developer", "developers", "forum", "community", "support", "help", "events", "shop", "store", "jobs", "careers"]);

// Path segments that name a section, not the page.
const GENERIC = new Set([
  "index", "default", "home", "main", "amp", "www", "web", "site", "en_us", "intl", "global",
  "article", "articles", "post", "posts", "p", "a", "story", "stories", "item", "items", "page", "pages",
  "view", "show", "content", "contents", "entry", "entries", "detail", "details", "s", "c", "r", "u", "d",
  "user", "users", "comments", "tag", "tags", "category", "categories", "topic", "topics", "section", "sections",
  "wiki", "lang", "id", "title", "explore", "directory", "browse", "list", "all", "archive", "archives",
  "day", "week", "month", "year", "taxonomy", "term", "terms", "node", "cat",
  "html", "htm", "php", "aspx", "course", "courses", "overview", "info", "landing", "general", "misc",
  "media", "resources", "resource", "library", "files", "file", "download", "downloads", "uploads", "assets",
]);

// Words a slug lowers that should not be: initialisms and a few names.
const CASED: Record<string, string> = Object.fromEntries(
  ("AI ML LLM LLMs GPU GPUs CPU CPUs TPU AWS GCP API APIs SDK FAQ PDF HTML CSS SQL JSON XML HTTP HTTPS URL URLs " +
   "USA UK EU UN NYC SF DC CEO CTO CFO COO PhD MBA DIY SEO UI UX iOS macOS iPhone iPad OAuth MCP IAS LED LCD TV HD " +
   "NASA FDA CDC NIH MIT UCLA UCSF USC NBA NFL MLB NHL FIFA GPT RAG NLP IP VPN DNS SSD RAM USB GPS EV EVs HVAC IoT " +
   "AR VR XR CRM ERP SaaS B2B B2C STEM AICTE STTP CNN BBC NPR IBM HP AMD NVIDIA OpenAI ChatGPT GitHub " +
   "YouTube LinkedIn JavaScript TypeScript PostgreSQL MySQL MongoDB Kubernetes COVID " +
   "NeurIPS ICML ICLR CVPR EMNLP IEEE ACM vLLM PyTorch TensorFlow NumPy LaTeX")
    .split(" ")
    .map((w) => [w.toLowerCase(), w]),
);

// Short words a headline leaves lower case, unless first.
const SMALL = new Set(["a", "an", "the", "and", "but", "or", "nor", "of", "to", "in", "on", "at", "by", "for", "with", "from", "as", "vs", "via", "per", "into", "than", "over"]);

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const decode = (s: string) => {
  try { return decodeURIComponent(s); } catch { return s; }
};

const EXT = /\.(s?html?|php\d?|aspx?|jsp|cfm|cgi|md|txt|xml|json|pdf)$/i;

// A locale: "en", "en-us", "pt_BR".
const isLocale = (s: string) => /^[a-z]{2}([-_][a-z]{2})?$/i.test(s);

// An id rather than words: a number, a hash, a code with digits and no word
// in it, or anything with query-ish punctuation.
function isId(s: string): boolean {
  if (/[=@&?;,]/.test(s)) return true;
  if (/^\d+$/.test(s)) return true;
  if (/^[0-9a-f]{8,}$/i.test(s)) return true;
  if (/^[a-z]{1,3}\d{4,}[a-z0-9]*$/i.test(s)) return true; // p1490, tt0111161, su870777; not h200
  if (s.length >= 6 && /\d/.test(s) && !/[a-z]{4,}/i.test(s)) return true;
  return false;
}

function siteOf(host: string): { site?: string; sub: string[] } {
  const labels = host.split(".");
  const n = labels.length >= 3 && labels[labels.length - 1].length === 2 && SLD.has(labels[labels.length - 2]) ? 3 : 2;
  const sub = labels.slice(0, -n);
  // The most specific name we know: "research.ibm.com" before "ibm.com".
  for (let i = 0; i <= labels.length - n; i++) {
    const name = KNOWN[labels.slice(i).join(".")];
    if (name) return { site: name, sub };
  }
  return { sub };
}

// One token of a slug, cased: an initialism as it is written, a model
// number in capitals (h100 -> H100), anything with a dot or inner capitals
// left alone (m5d.24xlarge, tripGrid).
function caseWord(w: string, first: boolean): string {
  const lw = w.toLowerCase();
  if (CASED[lw]) return CASED[lw];
  if (/^[a-z]{1,2}\d{1,4}[a-z]?$/i.test(w)) return w.toUpperCase();
  if (/[.\d]/.test(w) || /[A-Z]/.test(w.slice(1))) return w;
  if (!first && SMALL.has(lw)) return lw;
  return lw[0].toUpperCase() + lw.slice(1);
}

// A path segment as a title: "humanity-s-last-exam-why-gpt-5-scored.html"
// -> "Humanity's Last Exam Why GPT-5 Scored".
function titleOf(seg: string): string {
  let s = decode(seg).replace(EXT, "");
  // A trailing id or locale glued to the slug: "great-article-48213",
  // "dachstein-7-en-us". A year, "annual-report-2022", stays.
  s = s.replace(/([-_](?=[0-9a-f]*\d)[0-9a-f]{6,}|[-_]\d{5,})+$/i, "");
  s = s.replace(/[-_](en|de|fr|es|it|nl|pt|ja|zh)([-_][a-z]{2})?$/i, "");
  const toks = s.split(/[-_+\s]+/).filter(Boolean);
  if (!toks.length) return "";
  const cased = /[A-Z]/.test(s) && /[a-z]/.test(s); // Wikipedia's Alan_Turing: cased already
  // Slugs split what was one word: "mcp_s" was "MCP's", "don-t" "don't",
  // "gpt-5" "GPT-5".
  const out: string[] = [];
  for (const t of toks) {
    const lt = t.toLowerCase(), prev = out[out.length - 1];
    if (prev && /[a-z]$/i.test(prev) && (lt === "s" || lt === "ll" || lt === "re" || lt === "ve" ||
        (lt === "t" && /(n|don|can|won)$/i.test(prev)))) {
      out[out.length - 1] = `${prev}'${lt}`;
    } else if (prev && /^\d{1,2}(\.\d)?$/.test(t) && /^(gpt|gemini|llama|claude|windows|python|php|version|v)$/i.test(prev)) {
      out[out.length - 1] = `${prev}-${t}`;
    } else out.push(t);
  }
  const title = out
    .map((t, i) => {
      if (cased) return CASED[t.toLowerCase()] ?? t;
      const [w, ...rest] = t.split(/(?=['-])/); // "mcp's" -> "mcp", "'s"; "gpt-5" -> "gpt", "-5"
      return caseWord(w, i === 0) + rest.join("").toLowerCase();
    })
    .join(" ");
  // Long slugs end at a word, not inside one.
  return title.length > 110 ? title.slice(0, title.lastIndexOf(" ", 108)).replace(/\s+\S{1,3}$/, "") + "…" : title;
}

const wordCount = (s: string) => s.split(" ").filter(Boolean).length;

function dateIn(path: string): { text: string; at: string } | null {
  // /2023/05/12/ or /2023/05/ in the path, or 2023-05-12 in a slug.
  const m = path.match(/\/((?:19|20)\d{2})\/(0?[1-9]|1[0-2])(?:\/(0?[1-9]|[12]\d|3[01]))?(?=\/|$)/) ??
    path.match(/((?:19|20)\d{2})-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])(?!\d)/);
  if (!m) return null;
  const y = m[1], mo = MONTHS[parseInt(m[2], 10) - 1], d = m[3] ? parseInt(m[3], 10) : 0;
  return { text: d ? `${mo} ${d}, ${y}` : `${mo} ${y}`, at: m[0] };
}

export function describe(raw: string): Description | null {
  let u: URL;
  try {
    u = new URL(/^https?:\/\//i.test(raw) ? raw : `https://${raw}`);
  } catch {
    return null;
  }
  const host = u.hostname.toLowerCase().replace(/^www\d?\./, "");
  const { site, sub } = siteOf(host);
  let path = u.pathname;
  const d: Description = { site };

  const date = dateIn(path);
  if (date) {
    d.date = date.text;
    path = path.replace(date.at, "/");
  }
  const segs = path.split("/").filter(Boolean);
  const lower = segs.map((s) => decode(s).toLowerCase().replace(EXT, ""));

  const lang = sub.length && LANG[sub[0]] && site ? LANG[sub[0]] : "";
  for (const s of sub) if (!d.kind && KIND_ON_SUBDOMAIN.has(s)) d.kind = KIND[s];
  if (/\.pdf$/i.test(u.pathname)) d.kind = "PDF";

  if (site === "GitHub" && segs.length >= 2) {
    d.title = `${segs[0]} / ${segs[1]}`;
    if (lower[2] === "issues") d.kind = "Issue";
    else if (lower[2] === "pull") d.kind = "Pull request";
  } else if (site === "Reddit" && lower[0] === "r" && segs[1]) {
    d.kind = `r/${segs[1]}`;
    if (lower[2] === "comments" && segs[4]) d.title = titleOf(segs[4]);
  } else if (site === "YouTube") {
    if (lower[0] === "watch" || host === "youtu.be") d.kind = "Video";
    else if (segs[0]?.startsWith("@")) d.title = segs[0];
  } else if (site === "arXiv" && (lower[0] === "abs" || lower[0] === "pdf") && segs[1]) {
    d.kind = "Paper";
    d.title = `arXiv:${segs[1].replace(/\.pdf$/i, "").replace(/v\d+$/, "")}`;
  } else {
    // The segments that are words about this page, in order; the first kind
    // the others name along the way.
    const words: string[] = [];
    for (let i = 0; i < segs.length; i++) {
      const l = lower[i];
      if (KIND[l]) { if (!d.kind) d.kind = KIND[l]; continue; }
      if (GENERIC.has(l) || isLocale(l) || isId(l) || l.length < 2) continue;
      const t = titleOf(segs[i]);
      if (t) words.push(t);
    }
    if (words.length) {
      const last = words[words.length - 1], prev = words[words.length - 2];
      // ".../stanford-university-law-school-corporate-governance/course":
      // the long segment is the page, the lone word after it a tab of it.
      if (prev && wordCount(last) <= 1 && wordCount(prev) >= 3) d.title = prev;
      // ".../palo_alto/pilates": a short last segment reads better after
      // its parent, as a breadcrumb.
      else if (prev && wordCount(last) <= 2 && wordCount(prev) <= 4) {
        const pp = words[words.length - 3];
        d.title = (pp && wordCount(pp) <= 3 && wordCount(prev) <= 2 ? `${pp} › ` : "") + `${prev} › ${last}`;
      } else d.title = last;
    }
  }
  if (lang) d.kind = d.kind ? `${lang} ${d.kind}` : lang;
  if (d.title && site && d.title.toLowerCase() === site.toLowerCase()) delete d.title;
  if (!d.title && !d.kind && !d.date && segs.length === 0 && !u.search) d.kind = "Home page";
  return d.site || d.kind || d.title || d.date ? d : null;
}
